import React from 'react';
import { AsChildSlot } from '@wix/headless-utils/react';
import { RicosViewer, type RichContent } from '@wix/ricos';
import {
  RequiredIndicator,
  type RequiredIndicatorPlacement,
} from '@wix/ambassador-forms-v4-form/types';

import { Field as CoreField } from './core-field';
import {
  FieldContext,
  useFieldContext,
  type FieldContextValue,
} from './context/field-context';
import { useFieldView } from '../contexts/form-view-context';

enum TestIds {
  fieldRoot = 'field-root',
  fieldLabel = 'field-label',
  fieldInputWrapper = 'field-input-wrapper',
  fieldInput = 'field-input',
  fieldError = 'field-error',
  fieldDescription = 'field-description',
}

export interface FieldProps {
  id: string;
  children: React.ReactNode;
  asChild?: boolean;
  className?: string;
}

interface FieldComponent
  extends React.ForwardRefExoticComponent<
    FieldProps & React.RefAttributes<HTMLElement>
  > {
  Label: typeof FieldLabel;
  InputWrapper: typeof FieldInputWrapper;
  Input: typeof FieldInput;
  Error: typeof FieldError;
  Description: typeof FieldDescription;
}

export interface FieldLabelProps {
  children: React.ReactNode;
  asChild?: boolean;
  className?: string;
}

export interface FieldInputWrapperProps {
  children: React.ReactNode;
  asChild?: boolean;
  className?: string;
}

export interface FieldInputProps {
  children: React.ReactNode;
  asChild?: boolean;
  className?: string;
  description?: React.ReactNode;
}

export interface FieldErrorProps {
  asChild?: boolean;
  className?: string;
  errorMessage?: string;
  children?: React.ReactNode;
}

export interface FieldDescriptionProps {
  asChild?: boolean;
  className?: string;
  description?: RichContent;
}

const FieldRoot = React.forwardRef<HTMLElement, FieldProps>((props, ref) => {
  const { id, children, asChild, className, ...otherProps } = props;

  const fieldView = useFieldView(id);
  const layout = fieldView?.layout;

  if (!layout) {
    return null;
  }

  return (
    <CoreField id={id} layout={layout}>
      {(fieldData) => {
        const contextValue: FieldContextValue = {
          gridStyles: {
            label: fieldData.gridStyles.label,
            input: fieldData.gridStyles.input,
          },
        };

        return (
          <FieldContext.Provider value={contextValue}>
            <AsChildSlot
              ref={ref}
              asChild={asChild}
              className={className}
              data-testid={TestIds.fieldRoot}
              {...otherProps}
            >
              {children}
            </AsChildSlot>
          </FieldContext.Provider>
        );
      }}
    </CoreField>
  );
});

FieldRoot.displayName = 'Form.Field';

const FieldLabelRoot = React.forwardRef<HTMLElement, FieldLabelProps>(
  (props, ref) => {
    const { children, asChild, className, ...otherProps } = props;
    const { gridStyles } = useFieldContext();

    return (
      <AsChildSlot
        ref={ref}
        asChild={asChild}
        className={className}
        style={gridStyles.label}
        data-testid={TestIds.fieldLabel}
        {...otherProps}
      >
        <div>{children}</div>
      </AsChildSlot>
    );
  },
);

FieldLabelRoot.displayName = 'Form.Field.Label';

export interface FieldLabelRequiredProps {
  required?: boolean;
  requiredIndicatorType?: RequiredIndicator;
  requiredIndicatorPlacement?: RequiredIndicatorPlacement;
  asChild?: boolean;
  className?: string;
}

export const FieldLabelRequired = React.forwardRef<
  HTMLSpanElement,
  FieldLabelRequiredProps
>((props, ref) => {
  const {
    required = false,
    requiredIndicatorType = RequiredIndicator.ASTERISK,
    asChild,
    className,
    ...otherProps
  } = props;

  if (
    !required ||
    requiredIndicatorType === RequiredIndicator.NONE ||
    requiredIndicatorType === RequiredIndicator.UNKNOWN_INDICATOR
  ) {
    return null;
  }

  return (
    <AsChildSlot
      ref={ref}
      asChild={asChild}
      className={className}
      {...otherProps}
    >
      <span>
        {requiredIndicatorType === RequiredIndicator.ASTERISK
          ? '*'
          : requiredIndicatorType === RequiredIndicator.TEXT
          ? '(Required)'
          : null}
      </span>
    </AsChildSlot>
  );
});

FieldLabelRequired.displayName = 'Form.Field.Label.Required';

interface FieldLabelComponent
  extends React.ForwardRefExoticComponent<
    FieldLabelProps & React.RefAttributes<HTMLElement>
  > {
  Required: typeof FieldLabelRequired;
}

export const FieldLabel = FieldLabelRoot as FieldLabelComponent;
FieldLabel.Required = FieldLabelRequired;

export const FieldInputWrapper = React.forwardRef<
  HTMLElement,
  FieldInputWrapperProps
>((props, ref) => {
  const { children, asChild, className, ...otherProps } = props;
  const { gridStyles } = useFieldContext();

  return (
    <AsChildSlot
      ref={ref}
      asChild={asChild}
      className={className}
      style={gridStyles.input}
      data-testid={TestIds.fieldInputWrapper}
      {...otherProps}
    >
      <div>{children}</div>
    </AsChildSlot>
  );
});

FieldInputWrapper.displayName = 'Form.Field.InputWrapper';

export const FieldInput = React.forwardRef<HTMLElement, FieldInputProps>(
  (props, ref) => {
    const { children, description, asChild, className, ...otherProps } = props;

    return (
      <AsChildSlot
        ref={ref}
        asChild={asChild}
        className={className}
        data-testid={TestIds.fieldInput}
        {...otherProps}
      >
        <div>{children}</div>
      </AsChildSlot>
    );
  },
);

FieldInput.displayName = 'Form.Field.Input';

export const FieldError = React.forwardRef<HTMLElement, FieldErrorProps>(
  (props, ref) => {
    const { errorMessage, asChild, className, children, ...otherProps } = props;

    if (!errorMessage && !children) {
      return null;
    }

    return (
      <AsChildSlot
        data-testid={TestIds.fieldError}
        ref={ref}
        asChild={asChild}
        className={className}
        {...otherProps}
      >
        {children || errorMessage}
      </AsChildSlot>
    );
  },
);

FieldError.displayName = 'Form.Field.Error';

export const FieldDescription = React.forwardRef<
  HTMLElement,
  FieldDescriptionProps
>((props, ref) => {
  const { description, asChild, className, ...otherProps } = props;

  if (!description) {
    return null;
  }

  return (
    <AsChildSlot
      data-testid={TestIds.fieldDescription}
      ref={ref}
      asChild={asChild}
      className={className}
      {...otherProps}
    >
      <RicosViewer content={description} />
    </AsChildSlot>
  );
});

FieldDescription.displayName = 'Form.Field.Description';

export const Field = FieldRoot as FieldComponent;
Field.Label = FieldLabel;
Field.InputWrapper = FieldInputWrapper;
Field.Input = FieldInput;
Field.Error = FieldError;
Field.Description = FieldDescription;
